import { Square } from "lucide-react";
import { Link } from "react-router";
import { toast } from "sonner";

import { isOptimistic, useStopTimer, useTimer } from "@/features/time/api";
import { LiveDot } from "@/features/time/components/LiveDot";
import { TimerClock } from "@/features/time/components/TimerClock";
import { formatDuration } from "@/shared/lib/duration";
import { timeErrorMessage } from "@/features/time/lib/errors";
import { targetLink } from "@/features/time/lib/links";
import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";

interface RunningTimerProps {
  /** Only the clock and the stop button, for the phone's top bar. */
  compact?: boolean;
  className?: string;
}

/**
 * The running timer as a pill for the app shell: a live clock, what is being timed (a link to
 * it) and a stop button. Shows nothing while no timer runs.
 */
export function RunningTimer({ compact = false, className }: RunningTimerProps) {
  const t = useT();
  const timer = useTimer();
  const stop = useStopTimer();
  const entry = timer.data;
  if (!entry) return null;

  const code = entry.task?.reference ?? entry.project.key;
  const title = entry.task?.title ?? entry.project.name;
  const onStop = () => {
    stop.mutate(undefined, {
      onSuccess: (entries) => {
        const minutes = entries.reduce((sum, item) => sum + (item.minutes ?? 0), 0);
        toast.success(t("time.timer.stopped", { duration: formatDuration(minutes, t), title }));
      },
      onError: (error) => {
        toast.error(t(timeErrorMessage(error)));
      },
    });
  };

  return (
    <div
      role="status"
      aria-label={t("time.timer.runningOn", { title })}
      className={cn(
        "flex h-8 max-w-full min-w-0 items-center gap-2 rounded-full border border-brand/25 bg-brand-soft ps-2.5 pe-1 text-brand-strong shadow-xs",
        className,
      )}
    >
      <LiveDot />
      <TimerClock startedAt={entry.started_at} className="text-[14px] text-brand-strong" />
      {compact ? null : (
        <Link
          to={targetLink(entry)}
          title={title}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded text-[13.5px] hover:underline"
        >
          <span dir="ltr" className="shrink-0 font-mono text-[12px] text-brand-strong/80">
            {code}
          </span>
          <span className="user-text min-w-0 truncate text-ink">{title}</span>
        </Link>
      )}
      <button
        type="button"
        onClick={onStop}
        disabled={stop.isPending || isOptimistic(entry)}
        aria-label={t("time.timer.stopNamed", { title })}
        title={t("time.timer.stop")}
        className="ms-auto grid size-6 shrink-0 place-items-center rounded-full bg-surface text-ink shadow-xs ring-1 ring-line transition-colors hover:text-danger disabled:opacity-50"
      >
        <Square className="size-2.5 fill-current" aria-hidden="true" />
      </button>
    </div>
  );
}
